import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import TextField from '@mui/material/TextField'
import { clientInputSchema, type ClientDto, type ClientInput } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'
import { ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof clientInputSchema>

const ENTITY_NUMBER_URL = 'https://secapp.taxes.gov.il/sr-login-internet-nev/main/identification'

const EMPTY: FormValues = {
  hebrewFirstName: '',
  hebrewLastName: '',
  firstName: '',
  lastName: '',
  nationalId: '',
  passport: '',
  country: '',
  email: '',
  phone: '',
  entityNumber: '',
}

const toFormValues = (client: ClientDto): FormValues => ({
  hebrewFirstName: client.hebrewFirstName ?? '',
  hebrewLastName: client.hebrewLastName ?? '',
  firstName: client.firstName ?? '',
  lastName: client.lastName ?? '',
  nationalId: client.nationalId ?? '',
  passport: client.passport ?? '',
  country: client.country ?? '',
  email: client.email ?? '',
  phone: client.phone ?? '',
  entityNumber: client.entityNumber ?? '',
})

type Props = {
  open: boolean
  client?: ClientDto | null
  onClose: () => void
  onCreated?: (client: ClientDto) => void
}

export function ClientDialog({ open, client, onClose, onCreated }: Props) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(client)

  const form = useForm<FormValues, unknown, ClientInput>({
    resolver: formResolver(clientInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    form.reset(client ? toFormValues(client) : EMPTY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.id])

  const save = useMutation({
    mutationFn: (values: ClientInput) =>
      isEdit
        ? api.patch<ClientDto>(`/clients/${client?.id}`, values)
        : api.post<ClientDto>('/clients', values),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      if (client) void queryClient.invalidateQueries({ queryKey: qk.client(client.id) })
      if (!isEdit) onCreated?.(saved)
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת לקוח' : 'לקוח חדש'}</DialogTitle>

        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="שם פרטי בעברית"
                autoFocus
                {...form.register('hebrewFirstName')}
                error={Boolean(errors.hebrewFirstName)}
                helperText={errors.hebrewFirstName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="שם משפחה בעברית" {...form.register('hebrewLastName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="שם פרטי באנגלית" {...form.register('firstName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="שם משפחה באנגלית" {...form.register('lastName')} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="מספר תעודת זהות"
                {...form.register('nationalId')}
                error={Boolean(errors.nationalId)}
                helperText={errors.nationalId?.message ?? 'חובה ת״ז או דרכון'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="מספר דרכון"
                {...form.register('passport')}
                error={Boolean(errors.passport)}
                helperText={errors.passport?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="מדינה" {...form.register('country')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="מספר ישות" {...form.register('entityNumber')} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="דואר אלקטרוני"
                type="email"
                {...form.register('email')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="טלפון נייד" {...form.register('phone')} />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            href={ENTITY_NUMBER_URL}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            sx={{ marginInlineEnd: 'auto' }}
          >
            הנפקת מס׳ ישות
          </Button>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
